import React, { useCallback } from 'react';

import CheckboxFormField from '@components/form-fields/CheckboxFormField';
import Loader from '@components/loader/Loader';
import { ITableRowSettings } from '@components/table';
import { EnhancedTableHeader } from '@components/table/widgets/shared-with-old';
import EnhancedTableBodyCell from '@components/table/widgets/shared-with-old/EnhancedTableBodyCell';
import { IAbstractRecord } from '@models';
import Error from '@mui/icons-material/Error';
import {
  lighten,
  TableBody,
  TableCell,
  TableRow,
  Tooltip
} from '@mui/material';
import { MAIN_THEME } from '@styles';
import { getRowConditions } from '@utils/table-utils';

import { IEnhancedRow } from '../types';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

interface IListingTableBodyWithCellInputsProps {
  emptyRows: number;
  handleClick(name: string, checked: boolean): void;
  handleUpdate?(
    rowKey: number,
    property: string,
    value: unknown,
    row?: IAbstractRecord
  ): void;
  handleCellValueChanged?(
    rowKey: number,
    property: string,
    value: unknown,
    rowId?: string,
    row?: IAbstractRecord
  ): void;
  headers: Record<string, EnhancedTableHeader>;
  rowSettings?: ITableRowSettings;
  loader?: boolean;
  dynamicRowMessage?: (row: IEnhancedRow) => string[];
  isSelected: (key: string) => boolean;
  rows: IEnhancedRow[];
  currencySymbol?: string;
  disableSelection?: boolean;
  forceShowSelectColumn?: boolean;
}

const useStyles = makeStyles()((theme) => ({
  bodyRoot: {
    '& tr:nth-of-type(odd)': {
      backgroundColor: MAIN_THEME.color.white98
    }
  },
  rowRoot: {
    '&.Mui-selected': {
      backgroundColor: lighten(MAIN_THEME.color.black, 0.85)
    },
    '&.MuiTableRow-hover:hover': {
      backgroundColor: lighten(MAIN_THEME.color.black, 0.85)
    }
  },
  cellRoot: {
    color: theme.palette.primary.main,
    fontSize: MAIN_THEME.fontSize.xs,
    lineHeight: '12px',
    height: 34,
    border: 'none',
    padding: '4px 2px'
  },
  checkboxCellRoot: {
    width: 14,
    padding: '0 14px 0 8px',
    border: 'none',
    height: 34,
    position: 'relative'
  },
  checkboxCellRootWithMessage: {
    padding: '0 23px 5px 8px'
  },
  noDataMessage: {
    textAlign: 'center',
    color: MAIN_THEME.color.white86
  },
  noDataCell: {
    color: theme.palette.primary.main,
    border: 'none',
    height: '34px',
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '12px'
  },
  errorIconStyle: {
    height: '15px',
    width: '15px',
    color: 'red',
    position: 'absolute',
    top: '10px',
    right: '8px',
    cursor: 'pointer'
  },
  paddedCellRoot: {
    padding: '0px 6px 0px 20px'
  }
}));

const ListingTableBodyWithCellInputs: React.FC<
  IListingTableBodyWithCellInputsProps
> = ({
  emptyRows,
  handleClick,
  headers,
  isSelected,
  rows,
  currencySymbol,
  rowSettings,
  handleUpdate,
  handleCellValueChanged,
  disableSelection,
  dynamicRowMessage,
  forceShowSelectColumn,
  loader
}) => {
  const { classes } = useStyles();
  const visibleHeaders = Object.values(headers).filter(
    (header) => !header?.hidden
  );

  const checkIfRowHasMessage = useCallback(
    (row: IEnhancedRow) => {
      if (dynamicRowMessage) {
        const dynamcMessages = dynamicRowMessage(row);
        if (dynamcMessages && dynamcMessages.length > 0) {
          return true;
        }
      }

      if (
        isSelected(row.key) &&
        Number(row.columns.OutstandingAmount) <
          Number(row.columns.CollectedAmount)
      ) {
        return true;
      }
      if (
        !isSelected(row.key) ||
        !rowSettings ||
        !rowSettings.conditions ||
        rowSettings.conditions.length === 0
      ) {
        return false;
      }

      return getRowConditions(rowSettings, row);
    },

    [rows, rowSettings]
  );

  const renderRow = (row: IEnhancedRow) => {
    let message = '';
    if (dynamicRowMessage) {
      const dynamcMessages = dynamicRowMessage(row);
      console.log(dynamcMessages);
      if (dynamcMessages && dynamcMessages.length > 0) {
        message = dynamcMessages[0];
      }
    }

    if (!message) {
      if (rowSettings && checkIfRowHasMessage(row)) {
        message = rowSettings.iconTooltip;
      }
    }
    if (message) {
      return (
        <Tooltip title={message}>
          <span>
            <Error className={classes.errorIconStyle} />
          </span>
        </Tooltip>
      );
    }

    return <></>;
  };

  return (
    <TableBody classes={{ root: classes.bodyRoot }}>
      {rows.length > 0 ? (
        rows.map((row, index) => {
          const isItemSelected = isSelected(row.key);
          const labelId = `enhanced-table-checkbox-${index}`;

          return (
            <TableRow
              hover
              role="checkbox"
              aria-checked={isItemSelected}
              tabIndex={-1}
              key={row.key}
              selected={isItemSelected}
              classes={{ root: classes.rowRoot }}
            >
              <TableCell
                padding="none"
                classes={{
                  root: clsx(
                    classes.checkboxCellRoot,
                    classes.checkboxCellRootWithMessage
                  )
                }}
              >
                {!disableSelection && (
                  <CheckboxFormField
                    title=""
                    checked={isItemSelected}
                    name={row.key}
                    onChange={handleClick}
                    inputOnly
                  />
                )}
                {renderRow(row)}
              </TableCell>
              {visibleHeaders.map((header, headerIndex) => {
                return (
                  <EnhancedTableBodyCell
                    selected={isItemSelected}
                    key={header.name + labelId}
                    row={row}
                    header={header}
                    currencySymbol={currencySymbol}
                    handleUpdate={handleUpdate}
                    handleCellValueChanged={handleCellValueChanged}
                    className={clsx({
                      [classes.paddedCellRoot]:
                        disableSelection &&
                        forceShowSelectColumn &&
                        !headerIndex
                    })}
                  />
                );
              })}
            </TableRow>
          );
        })
      ) : (
        <TableRow>
          <TableCell
            rowSpan={2}
            colSpan={visibleHeaders.length > 0 ? visibleHeaders.length + 1 : 1}
            align="center"
            classes={{ root: classes.noDataCell }}
          >
            {loader ? <Loader /> : 'No Data'}
          </TableCell>
        </TableRow>
      )}
      {emptyRows > 0 && <TableRow style={{ height: 34 * emptyRows }} />}
    </TableBody>
  );
};

export default ListingTableBodyWithCellInputs;
